import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import Text from 'components/inputs/text';
import Tooltip from 'components/layout/tooltip';
import { SystemOptionType } from 'data/types';
import { useRenameCustomSystemMutation } from 'features/api/project-api';
import { updateSystemOptionName } from 'features/systems';
import { useState } from 'react';

const RENAME_SIZE_LIMIT = 25;

type EditableSystemNameProps = Readonly<{
  system: SystemOptionType;
  displayName: string;
  showTopMatch: boolean;
}>;

export default function EditableSystemName({
  system,
  displayName,
  showTopMatch
}: EditableSystemNameProps) {
  const dispatch = useAppDispatch();
  const systemOptions = useAppSelector((state) => state.systems.systemOptions);
  const [renameCustomSystem] = useRenameCustomSystemMutation();
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');

  const isCustom = system.tags.includes('Custom');
  const configId = system.configuration.uniqueId;

  const handleRenameClick = () => {
    setRenaming(true);
    setNewName(system.name);
  };

  const handleUpdateName = async () => {
    const trimmed = newName.trim();
    if (trimmed !== '') {
      try {
        await renameCustomSystem({
          uniqueId: system.uniqueId,
          configurationUniqueId: configId,
          name: trimmed
        }).unwrap();
      } catch (err) {
        console.error('Failed to rename custom system', err);
        setRenaming(false);
        return;
      }
      const index = (systemOptions[configId] ?? []).findIndex(
        (o) => o.uniqueId === system.uniqueId
      );
      dispatch(
        updateSystemOptionName({
          configId,
          index,
          uniqueId: system.uniqueId,
          newName: trimmed
        })
      );
    }
    setRenaming(false);
  };

  const handleRenameKeyDown = (key: string) => {
    if (key === 'Enter') {
      handleUpdateName();
    }
  };

  if (renaming) {
    return (
      <div className='relative mt-1.5 w-full max-w-sm'>
        <Text
          size='sm'
          onInput={(value) =>
            setNewName((value ?? '').slice(0, RENAME_SIZE_LIMIT))
          }
          value={newName}
          onBlur={() => handleUpdateName()}
          onKeyDown={(e) => handleRenameKeyDown(e.key)}
          autoFocus
          autoSelect
          controlled
        />
        <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-sm text-gray-500'>
          {newName.length}/{RENAME_SIZE_LIMIT}
        </div>
      </div>
    );
  }

  return (
    <h1 className='inline-flex items-center text-3xl font-bold text-gray-900'>
      {displayName}
      {isCustom && (
        <button
          type='button'
          onClick={() => handleRenameClick()}
          className='relative ml-2 text-gray-400 hover:text-gray-600'
        >
          <PencilSquareIcon className='h-6 w-6' />
          <Tooltip text='Rename' />
        </button>
      )}
      {showTopMatch && (
        <span className='ml-2 text-base text-purple-500'>• Your Top Match</span>
      )}
    </h1>
  );
}
